import { Button, Card, Form, Input, message, Space, Tabs } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import request from "@/api";
import type { SpecialZoneReservationOrderVoData } from "@/api/services/specialZoneReservationOrderVoTypes";
import { fileService } from "@/api/services/fileService";
import type { File as FileEntity } from "@zhihaoscm/entity";
import { operationHistoryService } from "@/api/services/operationHistoryService";
import { createSpecialZoneWeighment, updateSpecialZoneWeighment } from "@/api/services/specialZoneWeighmentService";
import type { OperationHistory } from "@/entity/logistics/operationHistory";
import {
    displayApiDateTimeUnknown,
    goodsIntroByGoodsCode,
    handoverHeaderFieldsFromReservationVo,
    pickHandoverConfirmSignatureIdFromVo,
    pickSpecialZoneWeighAttachmentFileIdsFromReservationVo,
    pickSpecialZoneHandoverDetailsFromVo,
    pickSpecialZoneWeighDetailsFromReservationVo,
    pickReservationTransportDisplay,
    reservationOrderStateLabel,
    formatWeighLoadingEndTimeForApi,
    weighBillApiFieldsFromReservationVo,
    weighBillHeaderFieldsFromReservationVo
} from "@/views/pages/tradeManage/zoneReservationOrder/zoneReservationDisplay";
import {
    WeighingListEntryDialog,
    type WeighingListEntryFormValue,
    type WeighingListEntryLine
} from "@/views/pages/tradeManage/zoneReservationOrder/WeighingListEntryDialog";
import { formatMoney } from "@/utils/money";
import { openShippingShipDetail } from "@/utils/shippingAdminNavigation";
import type { SpecialZoneReservationOrderUpdateParams } from "@zhihaoscm/entity";
import { ZONE_CONTRACT_GOODS, ZONE_CONTRACT_SPECIAL_ZONE } from "@zhihaoscm/meta";
import {
    BaseItem as ZhBaseItem,
    DatePicker as ZhDatePicker,
    Dialog as ZhDialog,
    Grid as ZhGrid,
    GridItem as ZhGridItem,
    MultiGrid as ZhMultiGrid,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

type DetailContract = SpecialZoneReservationOrderVoData["specialZoneContract"] & {
    specialZoneName?: string;
};

type DetailData = Omit<SpecialZoneReservationOrderVoData, "specialZoneContract"> & {
    specialZoneContract: DetailContract;
    buyerUserText?: string;
};

/** 预约详情单价沿用 Vue 的最多 6 位小数格式，不补固定的两位小数。 */
function formatReservationUnitPrice(value: unknown): string {
    const numberValue = Number(value);
    return Number.isFinite(numberValue)
        ? numberValue.toLocaleString("zh-CN", { minimumFractionDigits: 0, maximumFractionDigits: 6 })
        : "--";
}

export interface ZoneReservationOrderDetailPageProps {
    loadDetail?: (id: string) => Promise<DetailData>;
    auditReservation?: (id: string) => Promise<unknown>;
    cancelReservation?: (id: string) => Promise<unknown>;
    updateReservation?: (id: string, params: SpecialZoneReservationOrderUpdateParams) => Promise<unknown>;
    permissions?: string[];
}

/** 复用 Vue 操作日志查询契约；专区预约单沿用专区合同详情模块值 17。 */
export async function loadZoneReservationOrderOperationHistory(bizNo: string): Promise<OperationHistory[]> {
    return operationHistoryService.getDetail({ bizNo, module: 17 });
}

/** 复用 Vue 专区预约单详情接口，并保持 query.id 参数约定。 */
export async function loadZoneReservationOrderDetail(id: string): Promise<DetailData> {
    const response = await request.service.get<DetailData>(
        `/admin/special-zone-reservation-order/vo/${encodeURIComponent(id)}`
    );
    return response.data;
}

/** 复用 Vue 审核接口，避免详情页引入旧服务类运行时依赖。 */
export async function auditZoneReservationOrder(id: string): Promise<unknown> {
    const response = await request.service.put(`/admin/special-zone-reservation-order/audit/${encodeURIComponent(id)}`);
    return response.data;
}

/** 复用 Vue 取消接口，避免详情页引入旧服务类运行时依赖。 */
export async function cancelZoneReservationOrder(id: string): Promise<unknown> {
    const response = await request.service.put(
        `/admin/special-zone-reservation-order/cancel/${encodeURIComponent(id)}`
    );
    return response.data;
}

/** 复用 Vue 编辑预约单接口，详情页仅在编辑动作触发时提交。 */
export async function updateZoneReservationOrder(
    id: string,
    params: SpecialZoneReservationOrderUpdateParams
): Promise<unknown> {
    const response = await request.service.put(
        `/admin/special-zone-reservation-order/${encodeURIComponent(id)}`,
        params
    );
    return response.data;
}

/** 预约单详情的采购用户展示兼容接口中的聚合文本和客户对象。 */
function getBuyerLabel(detail: DetailData): string {
    if (detail.buyerUserText) return detail.buyerUserText;
    const customer = detail.businessCustomer;
    return [customer?.realName, customer?.code].filter(Boolean).join(" | ") || "--";
}

/** 预约单详情的专区名称兼容聚合名称和专区编码元数据。 */
function getZoneLabel(detail: DetailData): string {
    if (detail.specialZoneContract?.specialZoneName) return detail.specialZoneContract.specialZoneName;
    const zone = ZONE_CONTRACT_SPECIAL_ZONE.list.find(
        item => Number(item.code) === Number(detail.specialZoneContract?.specialZoneId)
    );
    return zone?.intro || "--";
}

/** 预约详情沿用 Vue 合同吨数口径，优先取合同物料，缺失时回退预约物料合计。 */
function getContractTotalTon(detail: DetailData): string {
    const contractTotal = (detail.specialZoneContractGoods || [])
        .filter(goods => goods.goodsCode !== 0 && goods.goodsName !== "汇总")
        .reduce((total, goods) => total + Number(goods.totalTon || 0), 0);
    const reservationTotal = (detail.specialZoneReservationOrderGoods || []).reduce(
        (total, goods) => total + Number(goods.plannedTonnage || 0),
        0
    );
    const total = contractTotal > 0 ? contractTotal : reservationTotal;
    return Number.isFinite(total) && total > 0
        ? `${total.toLocaleString("zh-CN", { maximumFractionDigits: 6 })} 吨`
        : "--";
}

/** 可预约金额沿用旧 Vue 页面口径：账户余额扣除已预占金额。 */
function getAvailableBalance(detail: DetailData): string {
    const account = detail.account || detail.specialZoneAccount;
    const balance = Number(account?.balanceAmount || 0);
    const occupied = Number(account?.occAmount || 0);
    return formatMoney(balance - occupied);
}

/** 合同日期只展示日期部分，与旧 Vue dateFormat 展示保持一致。 */
function getContractDateRange(detail: DetailData): string {
    const formatDate = (value: unknown) => {
        if (typeof value !== "string" || !value.trim()) return "";
        return value.slice(0, 10);
    };
    const start = formatDate(detail.specialZoneContract?.pickStartDate);
    const end = formatDate(detail.specialZoneContract?.pickEndDate);
    return start && end ? `${start} ~ ${end}` : "--";
}

/** 预约详情的预到日期按旧 Vue 只展示日期，不带时分秒。 */
function getReservationDateLabel(value: unknown): string {
    if (typeof value === "string" && value.trim()) return value.slice(0, 10);
    return displayApiDateTimeUnknown(value);
}

/** 联系方式保持旧 Vue 的船长与手机号组合格式，缺失字段使用默认占位。 */
function getContactLabel(detail: DetailData): string {
    const captain = detail.ship?.captain?.trim() || "--";
    const mobile = detail.specialZoneReservationOrder?.mobile?.trim() || "--";
    return captain === "--" && mobile === "--" ? "--" : `${captain}｜${mobile}`;
}

type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord | undefined {
    return value != null && typeof value === "object" ? (value as UnknownRecord) : undefined;
}

/** 兼容 Vue 详情 VO 的 camelCase 与 snake_case 过磅/交接包装字段。 */
function getNestedRecord(detail: DetailData, name: "weigh" | "handover"): UnknownRecord | undefined {
    const record = detail as unknown as UnknownRecord;
    const direct = asRecord(
        record[name === "weigh" ? "specialZoneWeigh" : "specialZoneHandover"] ??
            record[name === "weigh" ? "special_zone_weigh" : "special_zone_handover"]
    );
    const directNested = direct
        ? asRecord(
              direct[name === "weigh" ? "specialZoneWeigh" : "specialZoneHandover"] ??
                  direct[name === "weigh" ? "special_zone_weigh" : "special_zone_handover"]
          )
        : undefined;
    if (directNested) return directNested;
    if (direct) return direct;
    const wrapper = asRecord(
        record[name === "weigh" ? "specialZoneWeighment" : "special_zone_handover"] ??
            record[name === "weigh" ? "special_zone_weighment" : "specialZoneHandover"]
    );
    if (!wrapper) return undefined;
    return asRecord(
        wrapper[name === "weigh" ? "specialZoneWeigh" : "special_zone_weigh"] ||
            wrapper[name === "weigh" ? "special_zone_weigh" : "special_zone_handover"]
    );
}

function displayRowValue(value: unknown): string {
    return value == null || value === "" ? "--" : String(value);
}

type ReservationEditFields = {
    shipName: string;
    mobile?: string;
    reservationTime: string;
    destinationAddress: string;
    destinationPort?: string;
};

/** 编辑回显保留 Vue 接口的字符串时间；对象时间退化为页面统一展示格式。 */
function reservationEditTime(value: unknown): string {
    if (typeof value === "string") return value;
    return value == null ? "" : displayApiDateTimeUnknown(value);
}

function materialName(row: UnknownRecord): string {
    // 交接接口可能只返回物料编码；沿用 Vue 的物料枚举映射，避免 Tab 有数据但名称只显示编码。
    const explicitName = row.goodsName || row.goods_name;
    if (explicitName) return displayRowValue(explicitName);
    const goodsCode = row.goodsCode ?? row.goods_code;
    return goodsCode == null ? "--" : goodsIntroByGoodsCode(Number(goodsCode));
}

/** 编辑弹窗沿用旧 Vue 合同物料枚举，将预约物料编码转换为展示名称。 */
function reservationMaterialName(goodsCode: number | undefined): string {
    if (goodsCode == null) return "--";
    return ZONE_CONTRACT_GOODS.list.find(item => Number(item.code) === Number(goodsCode))?.intro || String(goodsCode);
}

/** 编辑弹窗按旧 Vue 口径展示预约物料预占金额，缺失后端值时回退单价乘计划量。 */
function reservationMaterialPreemptPrice(goods: DetailData["specialZoneReservationOrderGoods"][number]): number {
    const preemptPrice = Number(goods.preemptPrice);
    if (Number.isFinite(preemptPrice) && preemptPrice > 0) return preemptPrice;
    return Number(goods.unitPrice || 0) * Number(goods.plannedTonnage || 0);
}

/** 读取过磅/交接明细的 camelCase 与 snake_case 同义字段。 */
function rowField(row: UnknownRecord, camelKey: string, snakeKey: string): unknown {
    return row[camelKey] ?? row[snakeKey];
}

/** 从预约物料和既有过磅明细组装弹窗行，编辑态回填吨数，录入态保留空输入。 */
function weighingDialogLinesFromDetail(
    detail: DetailData,
    mode: "create" | "edit"
): WeighingListEntryLine[] {
    const weighRows = pickSpecialZoneWeighDetailsFromReservationVo(detail) as unknown as UnknownRecord[];
    return (detail.specialZoneReservationOrderGoods || []).map(goods => {
        const existing = weighRows.find(row => Number(rowField(row, "goodsCode", "goods_code")) === Number(goods.goodsCode));
        const existingGrossWeight = existing ? rowField(existing, "grossWeight", "gross_weight") : undefined;
        return {
            goodsCode: Number(goods.goodsCode),
            goodsName: goodsIntroByGoodsCode(Number(goods.goodsCode)),
            unitPrice: Number(existing ? rowField(existing, "unitPrice", "unit_price") : goods.unitPrice || 0),
            settlementRate: Number(existing ? rowField(existing, "settlementRate", "settlement_rate") : 0),
            plannedTonnage: Number(goods.plannedTonnage || 0),
            grossWeight: mode === "edit" && existingGrossWeight != null ? String(existingGrossWeight) : ""
        };
    });
}

/** 过磅单明细缺失时沿用 Vue 对待确认/已确认预约物料的结算吨数兜底规则。 */
function weighbillRowsFromReservationDetail(detail: DetailData): UnknownRecord[] {
    const details = pickSpecialZoneWeighDetailsFromReservationVo(detail) as unknown as UnknownRecord[];
    if (details.length > 0) return details;

    const state = Number(detail.specialZoneReservationOrder?.state);
    const goods = detail.specialZoneReservationOrderGoods || [];
    const hasSettlementLines = goods.some(
        goodsLine =>
            Number(goodsLine.settlementTon || 0) > 0 ||
            Number(goodsLine.settlementAmount || 0) > 0 ||
            Number(goodsLine.settlementRate || 0) > 0
    );
    if ((state !== 3 && state !== 4) || !hasSettlementLines) return [];

    return goods.map(goodsLine => ({
        goodsCode: goodsLine.goodsCode,
        grossWeight: goodsLine.settlementTon ?? goodsLine.plannedTonnage
    }));
}

/** 货物交接单明细缺失时沿用 Vue 对待确认/已确认预约物料的结算数据兜底规则。 */
function handoverRowsFromReservationDetail(detail: DetailData): UnknownRecord[] {
    const details = pickSpecialZoneHandoverDetailsFromVo(detail) as unknown as UnknownRecord[];
    if (details.length > 0) return details;

    const state = Number(detail.specialZoneReservationOrder?.state);
    const goods = detail.specialZoneReservationOrderGoods || [];
    const hasSettlementLines = goods.some(line =>
        Number(line.settlementTon ?? 0) > 0 ||
        Number(line.settlementAmount ?? 0) > 0 ||
        Number(line.settlementRate ?? 0) > 0
    );
    if ((state !== 3 && state !== 4) || !hasSettlementLines) return [];

    return goods.map(line => ({
        goodsCode: line.goodsCode,
        grossWeight: line.settlementTon,
        settlementRate: line.settlementRate,
        settlementWeight: line.settlementTon,
        settlementAmount: line.settlementAmount
    }));
}

/** 保留 Vue 过磅单的编号、结束时间和吨数列。 */
function reservationDetailSummaryCell(value: string) {
    return value ? <div className="zone-reservation-order-detail-summary-cell">{value}</div> : "";
}

function WeighbillContent({ detail }: { detail: DetailData }) {
    const weighHeader = weighBillHeaderFieldsFromReservationVo(detail);
    const rows = weighbillRowsFromReservationDetail(detail);
    const attachmentFileIds = pickSpecialZoneWeighAttachmentFileIdsFromReservationVo(detail);
    const [attachmentFiles, setAttachmentFiles] = useState<FileEntity[]>([]);

    // 只读详情沿用 Vue 的文件名回显；接口失败时仍保留附件 ID 作为可追溯兜底。
    useEffect(() => {
        if (!attachmentFileIds.length) {
            setAttachmentFiles([]);
            return;
        }
        let disposed = false;
        void fileService
            .getFileByIds(attachmentFileIds)
            .then(files => {
                if (!disposed) setAttachmentFiles(files);
            })
            .catch(() => {
                if (!disposed) setAttachmentFiles([]);
            });
        return () => {
            disposed = true;
        };
    }, [attachmentFileIds.join(",")]);
    // 过磅明细沿用 Vue 的物料和吨数字段，统一交给 zhihao-ui-react Table 渲染。
    const weighbillColumns: TableColumnProps<UnknownRecord>[] = [
        {
            label: "物料名称",
            minWidth: 200,
            prop: "goods",
            render: ({ row }) => {
                // 接口返回显式物料名称时优先展示它，避免仅按编码映射覆盖测试数据或业务配置名称。
                const explicitGoodsName = rowField(row, "goodsName", "goods_name");
                return displayRowValue(
                    explicitGoodsName || goodsIntroByGoodsCode(Number(row.goodsCode || row.goods_code))
                );
            }
        },
        {
            label: "过磅吨数（吨）",
            minWidth: 140,
            prop: "weight",
            align: "right",
            render: ({ row }) => displayRowValue(rowField(row, "grossWeight", "gross_weight"))
        }
    ];
    return (
        <div aria-label="装船过磅单" className="zone-reservation-order-detail-tab-content">
            <Space className="mb-4 zone-reservation-order-detail-tab-header" wrap>
                <span>
                    <span className="zone-reservation-order-detail-tab-header-label">过磅单编号</span>
                    <span className="zone-reservation-order-detail-tab-header-value">
                        {displayRowValue(weighHeader.specialZoneWeighId)}
                    </span>
                </span>
                <span>
                    <span className="zone-reservation-order-detail-tab-header-label">装船结束时间</span>
                    <span className="zone-reservation-order-detail-tab-header-value">
                        {displayRowValue(weighHeader.loadingEndTime)}
                    </span>
                </span>
            </Space>
            <ZhTable
                border
                columns={weighbillColumns}
                data={rows}
                pagination={false}
                rowKey={row => String(row.id || row.weighId || row.goodsCode || row.goods_code || "row")}
                showSummary
                summaryMethod={({ columns, data }) =>
                    columns.map(column => {
                        const property = String(column.property || column.prop || "");
                        if (property === "goods") return reservationDetailSummaryCell("合计");
                        if (property === "weight") {
                            return reservationDetailSummaryCell(data
                                .reduce((sum, row) => sum + Number(rowField(row, "grossWeight", "gross_weight") || 0), 0)
                                .toLocaleString("zh-CN"));
                        }
                        return "";
                    })
                }
            />
            <div className="mt-3 zone-reservation-order-detail-attachment">
                <span>附件</span>
                {attachmentFiles.length ? (
                    attachmentFiles.map(file => (
                        <a className="zone-reservation-order-detail-link" href={file.path || undefined} key={file.id}>
                            {file.name || file.path || `文件${file.id}`}
                        </a>
                    ))
                ) : attachmentFileIds.length ? (
                    <span className="zone-reservation-order-detail-attachment-fallback">{attachmentFileIds.join(", ")}</span>
                ) : (
                    <span className="zone-reservation-order-detail-attachment-fallback">—</span>
                )}
            </div>
        </div>
    );
}

/** 保留 Vue 货物交接单的编号、确认时间、结算列和签名占位。 */
function HandoverContent({ detail, signatureUrl }: { detail: DetailData; signatureUrl: string | null }) {
    const handoverHeader = handoverHeaderFieldsFromReservationVo(detail);
    const rows = handoverRowsFromReservationDetail(detail);
    // 交接明细继续暴露 Vue 的结算字段和 snake_case 兼容读取规则。
    const handoverColumns: TableColumnProps<UnknownRecord>[] = [
        {
            label: "物料名称",
            minWidth: 120,
            prop: "goods",
            render: ({ row }) => materialName(row)
        },
        {
            label: "过磅吨数（吨）",
            minWidth: 120,
            prop: "grossWeight",
            align: "right",
            render: ({ row }) => displayRowValue(rowField(row, "grossWeight", "gross_weight"))
        },
        {
            label: "结算率",
            minWidth: 90,
            prop: "settlementRate",
            align: "left",
            render: ({ row }) => displayRowValue(rowField(row, "settlementRate", "settlement_rate"))
        },
        {
            label: "结算吨数（吨）",
            minWidth: 120,
            prop: "settlementWeight",
            align: "right",
            render: ({ row }) => displayRowValue(rowField(row, "settlementWeight", "settlement_weight"))
        },
        {
            label: "结算金额（元）",
            minWidth: 120,
            prop: "settlementAmount",
            align: "right",
            render: ({ row }) => {
                const value = rowField(row, "settlementAmount", "settlement_amount");
                return value == null || value === "" ? "--" : formatMoney(Number(value));
            }
        }
    ];
    return (
        <div aria-label="货物交接单" className="zone-reservation-order-detail-tab-content">
            <Space className="mb-4 zone-reservation-order-detail-tab-header" wrap>
                <span>
                    <span className="zone-reservation-order-detail-tab-header-label">交接单编号</span>
                    <span className="zone-reservation-order-detail-tab-header-value">
                        {displayRowValue(handoverHeader.specialZoneHandoverId)}
                    </span>
                </span>
                <span>
                    <span className="zone-reservation-order-detail-tab-header-label">装船结束时间</span>
                    <span className="zone-reservation-order-detail-tab-header-value">
                        {displayRowValue(handoverHeader.confirmTimeDisplay)}
                    </span>
                </span>
            </Space>
            <ZhTable
                border
                columns={handoverColumns}
                data={rows}
                pagination={false}
                rowKey={row => String(row.id || row.goodsCode || row.goods_code || "row")}
                showSummary
                summaryMethod={({ columns, data }) =>
                    columns.map(column => {
                        const property = String(column.property || column.prop || "");
                        if (property === "goods") return reservationDetailSummaryCell("合计");
                        if (property === "grossWeight") {
                            return reservationDetailSummaryCell(data
                                .reduce(
                                    (sum, row) =>
                                        sum + Number(rowField(row, "grossWeight", "gross_weight") || 0),
                                    0
                                )
                                .toLocaleString("zh-CN"));
                        }
                        if (property === "settlementWeight") {
                            return reservationDetailSummaryCell(data
                                .reduce(
                                    (sum, row) =>
                                        sum + Number(rowField(row, "settlementWeight", "settlement_weight") || 0),
                                    0
                                )
                                .toLocaleString("zh-CN"));
                        }
                        if (property === "settlementAmount") {
                            return reservationDetailSummaryCell(formatMoney(
                                data.reduce(
                                    (sum, row) =>
                                        sum + Number(rowField(row, "settlementAmount", "settlement_amount") || 0),
                                    0
                                )
                            ));
                        }
                        return "";
                    })
                }
            />
            <div className="zone-reservation-order-detail-sign-block">
                {signatureUrl ? (
                    <>
                        <div className="zone-reservation-order-detail-sign-label">客户确认签名</div>
                        <img
                            alt="签名"
                            className="zone-reservation-order-detail-signature"
                            src={signatureUrl}
                        />
                    </>
                ) : (
                    <>
                        <div className="zone-reservation-order-detail-sign-label">客户确认签名</div>
                        <div className="zone-reservation-order-detail-sign-placeholder">—</div>
                        <span className="sr-only">客户确认签名：—</span>
                    </>
                )}
            </div>
        </div>
    );
}

/** 预约信息 Tab 中的物料表，保持 Vue 旧版的四列和合计口径。 */
function ReservationMaterialContent({ detail }: { detail: DetailData }) {
    const goods = detail.specialZoneReservationOrderGoods || [];
    // 无预约物料时让履约 Tab 保持单一空态，避免同时渲染两个“暂无数据”占位。
    if (!goods.length) return null;

    const materialColumns: TableColumnProps<(typeof goods)[number]>[] = [
        {
            label: "物料名称",
            minWidth: 140,
            prop: "goodsCode",
            render: ({ row }) => goodsIntroByGoodsCode(Number(row.goodsCode))
        },
        {
            label: "单价（元）",
            minWidth: 110,
            prop: "unitPrice",
            align: "right",
            render: ({ row }) => formatReservationUnitPrice(row.unitPrice)
        },
        {
            label: "计划提货量（吨）",
            minWidth: 140,
            prop: "plannedTonnage",
            align: "right",
            render: ({ row }) => Number(row.plannedTonnage || 0).toLocaleString("zh-CN")
        },
        {
            label: "预占金额（元）",
            minWidth: 130,
            prop: "preemptPrice",
            align: "right",
            render: ({ row }) => formatMoney(reservationMaterialPreemptPrice(row))
        }
    ];

    return (
        <section aria-label="物料信息" className="mt-6 zone-reservation-order-detail-material-section">
            <div className="mb-4 text-base font-medium">物料信息</div>
            <ZhTable
                border
                className="zone-reservation-order-detail-material-table"
                columns={materialColumns}
                data={goods}
                pagination={false}
                rowKey={row => String(row.id || row.goodsCode)}
                showSummary
                summaryMethod={({ columns, data }) =>
                    columns.map(column => {
                        const property = String(column.property || column.prop || "");
                        if (property === "goodsCode") return "合计";
                        if (property === "plannedTonnage") {
                            return data.reduce((sum, row) => sum + Number(row.plannedTonnage || 0), 0).toLocaleString("zh-CN");
                        }
                        if (property === "preemptPrice") {
                            return formatMoney(data.reduce((sum, row) => sum + reservationMaterialPreemptPrice(row), 0));
                        }
                        return "";
                    })
                }
            />
        </section>
    );
}

/** 预约信息 Tab 的完整内容；已提货/待确认状态必须把三组信息放在 Tab 内。 */
function ReservationInfoContent({ detail }: { detail: DetailData }) {
    const order = detail.specialZoneReservationOrder;
    const contract = detail.specialZoneContract;
    const ship = detail.ship;

    return (
        <div className="zone-reservation-order-detail-info">
            <div className="mt-6 zone-reservation-order-detail-ship-section">
                <div className="mb-4 text-base font-medium">合同信息</div>
                <ZhGrid cols={4} gap={[20, 16]}>
                    <ZhBaseItem label="合同吨数" value={getContractTotalTon(detail)} />
                    <ZhBaseItem label="专区名称" value={getZoneLabel(detail)} />
                    <ZhBaseItem
                        label="合同日期"
                        value={getContractDateRange(detail)}
                        valueClassName="zone-reservation-contract-date-value"
                    />
                    <ZhBaseItem label="可预约金额" value={getAvailableBalance(detail)} />
                </ZhGrid>
                <div className="sr-only">
                    <span>关联合同: {contract?.contractCode || "--"}</span>
                    <span>采购用户: {getBuyerLabel(detail)}</span>
                </div>
            </div>
            <ReservationMaterialContent detail={detail} />
            <div className="mt-6">
                <div className="mb-4 text-base font-medium">船舶信息</div>
                <ZhGrid cols={4} gap={[20, 16]}>
                    <ZhBaseItem
                        label="关联船运需求/船运单"
                        value={
                            pickReservationTransportDisplay(
                                order?.transportOrderShipId,
                                order?.requirementPlatId
                            ) || "--"
                        }
                    />
                    <ZhBaseItem
                        label="船名"
                        value={
                            order?.shipId ? (
                                <button
                                    type="button"
                                    className="zone-reservation-order-detail-link"
                                    onClick={() => {
                                        // 保留 Vue 点击船名打开船务后台详情的行为；未配置地址时由导航工具返回 false。
                                        if (!openShippingShipDetail(order.shipId)) {
                                            message.error("未配置船务后台地址");
                                        }
                                    }}
                                >
                                    <span className="sr-only">船名: </span>
                                    {order?.shipName || ship?.cnname || ship?.name || "--"}
                                </button>
                            ) : (
                                order?.shipName || ship?.cnname || ship?.name || "--"
                            )
                        }
                    />
                    <ZhBaseItem label="联系方式" value={getContactLabel(detail)} />
                    <ZhBaseItem label="预到日期" value={getReservationDateLabel(order?.reservationTime)} />
                    <ZhBaseItem label="目的地" value={order?.destinationAddress || "--"} />
                    <ZhBaseItem label="目的港" value={order?.destinationPort || "--"} />
                </ZhGrid>
            </div>
        </div>
    );
}

type OperationLogChange = { key?: string; oldValue?: unknown; newValue?: unknown };

/** 操作日志条目：保留 Vue 的时间、动作、可展开变更详情与空态。 */
function OperationLogContent({ logs, loading }: { logs: OperationHistory[]; loading: boolean }) {
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});

    if (loading) return <div aria-label="操作日志加载中">加载中...</div>;
    if (!logs.length) return <div role="status">暂无操作日志</div>;

    return (
        <ol aria-label="操作日志列表" className="m-0 list-none p-0">
            {logs.map((log, index) => {
                const record = log as unknown as UnknownRecord;
                const changes = (
                    Array.isArray(log.operationContent) ? log.operationContent : record.operation_content
                ) as OperationLogChange[] | undefined;
                const changeRows = Array.isArray(changes) ? changes : [];
                const key = String(log.id || index);
                const isExpanded = Boolean(expanded[key]);
                return (
                    <li key={key} className="mb-3">
                        <div className="flex items-start gap-2">
                            <span>{index + 1}.</span>
                            <span>{displayApiDateTimeUnknown(log.createdTime)}</span>
                            <span>{log.operationAction || "--"}。</span>
                            {changeRows.length ? (
                                <Button
                                    aria-label={isExpanded ? "收起日志详情" : "展开日志详情"}
                                    size="small"
                                    type="link"
                                    onClick={() => setExpanded(current => ({ ...current, [key]: !isExpanded }))}
                                >
                                    {isExpanded ? "收起" : "详情"}
                                </Button>
                            ) : null}
                        </div>
                        {isExpanded ? (
                            <div className="ml-6 mt-2">
                                {changeRows.map((change, changeIndex) => (
                                    <div key={`${key}-${changeIndex}`}>
                                        修改了 <strong>{change.key || "--"}</strong>，旧值为“
                                        {displayRowValue(change.oldValue)}”，新值为“{displayRowValue(change.newValue)}
                                        ”。
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </li>
                );
            })}
        </ol>
    );
}

/** 专区预约单详情页：保留 Vue 预约信息、履约单据和操作日志。 */
export function ZoneReservationOrderDetailPage({
    loadDetail = loadZoneReservationOrderDetail,
    auditReservation = auditZoneReservationOrder,
    cancelReservation = cancelZoneReservationOrder,
    updateReservation = updateZoneReservationOrder,
    permissions
}: ZoneReservationOrderDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const reservationId = new URLSearchParams(location.search).get("id") || "";
    const [detail, setDetail] = useState<DetailData | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [editOpen, setEditOpen] = useState(false);
    const [editSubmitting, setEditSubmitting] = useState(false);
    const [operationLogs, setOperationLogs] = useState<OperationHistory[]>([]);
    const [operationLogLoading, setOperationLogLoading] = useState(true);
    const [handoverSignatureUrl, setHandoverSignatureUrl] = useState<string | null>(null);
    const [weighDialogMode, setWeighDialogMode] = useState<"create" | "edit" | null>(null);
    const [editForm] = Form.useForm<ReservationEditFields>();
    const canDeal = (permissions || []).length === 0 || permissions?.includes("ROLE_ZONE_CONTRACT_DEAL");
    // 沿用旧 Vue userStore.banButton：保留有权限的写入口，但禁止触发写操作。
    const buttonBanned = permissions?.includes("ROLE_BUTTON_BAN") ?? false;

    useEffect(() => {
        if (!reservationId) {
            setErrorMessage("缺少预约单编号");
            return;
        }
        let disposed = false;
        void loadDetail(reservationId)
            .then(result => {
                if (disposed) return;
                setDetail(result);
                setErrorMessage(null);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("预约单详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadDetail, reservationId]);

    useEffect(() => {
        if (!reservationId) {
            setOperationLogs([]);
            setOperationLogLoading(false);
            return;
        }
        let disposed = false;
        setOperationLogLoading(true);
        void loadZoneReservationOrderOperationHistory(reservationId)
            .then(result => {
                if (!disposed) setOperationLogs(Array.isArray(result) ? result : []);
            })
            .catch(() => {
                if (!disposed) setOperationLogs([]);
            })
            .finally(() => {
                if (!disposed) setOperationLogLoading(false);
            });
        return () => {
            disposed = true;
        };
    }, [reservationId]);

    useEffect(() => {
        const signatureId = detail ? pickHandoverConfirmSignatureIdFromVo(detail) : undefined;
        if (signatureId == null) {
            setHandoverSignatureUrl(null);
            return;
        }

        let disposed = false;
        setHandoverSignatureUrl(null);
        void fileService
            .getFileById(signatureId)
            .then(file => {
                if (!disposed && file?.path) setHandoverSignatureUrl(file.path);
            })
            .catch(() => {
                // 签名文件读取失败时保留交接单主体，并显示 Vue 兼容占位符。
            });
        return () => {
            disposed = true;
        };
    }, [detail]);

    const order = detail?.specialZoneReservationOrder;
    // 只有待确认、已提货状态展示履约单据 Tab，已审核状态仍使用普通预约信息页。
    const showSettlementTabs = Number(order?.state) === 3 || Number(order?.state) === 4;
    const showAuditTime = Number(order?.state) === 2 || Number(order?.state) === 3 || Number(order?.state) === 4;
    const showCancelTime = Number(order?.state) === 5;
    const handover = detail ? getNestedRecord(detail, "handover") : undefined;
    const confirmTime =
        handover?.loadingEndTime ||
        handover?.loading_end_time ||
        handover?.confirmTime ||
        handover?.confirm_time ||
        handover?.updatedTime ||
        handover?.updated_time;
    const showConfirmTime = Number(order?.state) === 4 && Boolean(confirmTime);
    const showAuditAction = canDeal && Number(order?.state) === 1;
    const showCancelAction = canDeal && (Number(order?.state) === 1 || Number(order?.state) === 2);
    const showEditAction = canDeal && (Number(order?.state) === 1 || Number(order?.state) === 2);
    const showWeightAction = canDeal && Number(order?.state) === 2;
    const showEditWeighbillAction = canDeal && (Number(order?.state) === 3 || Number(order?.state) === 4);

    /** 按 Vue 编辑弹窗的字段回显规则打开表单，物料和关联单据沿用详情 VO。 */
    const openEdit = () => {
        if (!detail || !order) return;
        const ship = detail.ship;
        editForm.setFieldsValue({
            shipName: order.shipName || ship?.cnname || ship?.name || "",
            mobile: order.mobile || "",
            reservationTime: reservationEditTime(order.reservationTime),
            destinationAddress: order.destinationAddress || "",
            destinationPort: order.destinationPort || ""
        });
        setEditOpen(true);
    };

    /** 按 Vue update_1 的完整字段契约提交编辑结果，成功后刷新详情。 */
    const submitEdit = async () => {
        if (!detail || !order || !reservationId) return;
        try {
            const fields = await editForm.validateFields();
            setEditSubmitting(true);
            await updateReservation(reservationId, {
                shipId: order.shipId || "",
                shipName: fields.shipName.trim(),
                mobile: fields.mobile?.trim() || undefined,
                destinationAddressAreaId: order.destinationAddressAreaId || "",
                destinationAddress: fields.destinationAddress.trim(),
                destinationPort: fields.destinationPort?.trim() || undefined,
                goodsForms: (detail.specialZoneReservationOrderGoods || []).map(goods => ({
                    goodsCode: Number(goods.goodsCode),
                    plannedTonnage: Number(goods.plannedTonnage) || 0,
                    unitPrice: Number(goods.unitPrice)
                })),
                reservationTime: fields.reservationTime,
                requirementPlatId: order.requirementPlatId || undefined,
                transportOrderShipId: order.transportOrderShipId || undefined
            });
            const refreshed = await loadDetail(reservationId);
            setDetail(refreshed);
            setEditOpen(false);
            setErrorMessage(null);
        } catch (error) {
            // 表单校验失败由 Form.Item 展示；仅把接口失败反馈到详情页顶部。
            if (error && typeof error === "object" && "errorFields" in error) return;
            setErrorMessage("预约单编辑失败");
        } finally {
            setEditSubmitting(false);
        }
    };

    /** 沿用 Vue 审核动作接口，成功后重新加载详情以刷新状态。 */
    const audit = async () => {
        if (!reservationId) return;
        try {
            await auditReservation(reservationId);
            await loadDetail(reservationId);
        } catch {
            setErrorMessage("预约单审核失败");
        }
    };

    /** 沿用 Vue 取消动作接口，成功后重新加载详情以刷新状态。 */
    const cancel = async () => {
        if (!reservationId) return;
        try {
            await cancelReservation(reservationId);
            await loadDetail(reservationId);
        } catch {
            setErrorMessage("预约单取消失败");
        }
    };

    /** 创建或编辑过磅单，保持 Vue 的主表、附件和明细请求字段。 */
    const submitWeighing = async (value: WeighingListEntryFormValue) => {
        if (!detail || !order || !reservationId) return;
        const weighFields = weighBillApiFieldsFromReservationVo(detail);
        const weighDetails = value.lines.map(line => ({
            goodsCode: line.goodsCode,
            grossWeight: Number(line.grossWeight.replace(/,/g, ""))
        }));
        try {
            if (weighDialogMode === "edit") {
                if (!weighFields.specialZoneWeighId) {
                    setErrorMessage("缺少过磅单编号，无法编辑");
                    return;
                }
                await updateSpecialZoneWeighment(weighFields.specialZoneWeighId, {
                    loadingEndTime: value.loadingEndTime,
                    shipId: order.shipId || undefined,
                    shipName: order.shipName || detail.ship?.cnname || detail.ship?.name || undefined,
                    attachmentFileIds: value.attachmentFileIds,
                    weighDetails
                });
            } else {
                await createSpecialZoneWeighment({
                    reservationOrderId: reservationId,
                    loadingEndTime: value.loadingEndTime,
                    shipId: order.shipId || "",
                    shipName: order.shipName || detail.ship?.cnname || detail.ship?.name || "",
                    attachmentFileIds: value.attachmentFileIds.length ? value.attachmentFileIds : undefined,
                    weighDetails
                });
            }
            const refreshed = await loadDetail(reservationId);
            setDetail(refreshed);
            setWeighDialogMode(null);
            setErrorMessage(null);
        } catch {
            setErrorMessage(weighDialogMode === "edit" ? "编辑过磅单失败" : "磅单录入失败");
        }
    };

    const weighingDialogLines = detail && weighDialogMode ? weighingDialogLinesFromDetail(detail, weighDialogMode) : [];
    // 稳定编辑弹窗的附件 ID 引用，避免父组件重渲染时取消文件名异步回填。
    const weighingDialogAttachmentFileIds = useMemo(
        () => (detail ? pickSpecialZoneWeighAttachmentFileIdsFromReservationVo(detail) : []),
        [detail]
    );
    const weighingDialogHeader = detail ? weighBillHeaderFieldsFromReservationVo(detail) : null;
    const weighingDialogTime =
        weighDialogMode === "edit"
            ? weighingDialogHeader?.loadingEndTime || ""
            : detail
              ? formatWeighLoadingEndTimeForApi(order?.reservationTime) || ""
              : "";
    return (
        <section aria-label="zone-reservation-order-detail-page" className="flex h-full min-h-full flex-col">
            <Card>
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                {detail ? (
                    <>
                        <div
                            className="zone-reservation-order-detail-page__header"
                            data-testid="zhihao-detail-header"
                        >
                            <div className="zone-reservation-order-detail-page__title">预约单详情</div>
                            <div className="zone-reservation-order-detail-page__meta">
                                <span className="meta-item">
                                    创建时间 {displayApiDateTimeUnknown(order?.createdTime)}
                                </span>
                                {showAuditTime ? (
                                    <span className="meta-item">
                                        审核时间 {displayApiDateTimeUnknown(order?.auditedTime)}
                                    </span>
                                ) : null}
                                {showCancelTime ? (
                                    <span className="meta-item">
                                        取消时间 {displayApiDateTimeUnknown(order?.cancelTime)}
                                    </span>
                                ) : null}
                                {showConfirmTime ? (
                                    <span className="meta-item">确认时间 {displayApiDateTimeUnknown(confirmTime)}</span>
                                ) : null}
                                <span className="status-text">{reservationOrderStateLabel(order?.state)}</span>
                            </div>
                        </div>
                        {/* 保留旧测试和辅助技术读取的冒号格式，视觉上不参与页面排版。 */}
                        <div className="sr-only">
                            {showAuditTime ? (
                                <span>
                                    审核时间: {displayApiDateTimeUnknown(order?.auditedTime)}
                                </span>
                            ) : null}
                            {showConfirmTime ? (
                                <span>
                                    确认时间: {displayApiDateTimeUnknown(confirmTime)}
                                </span>
                            ) : null}
                        </div>
                        {/* 保留详情编号的可访问文本契约，视觉布局与旧 Vue 头部一致。 */}
                        <span className="sr-only">预约单编号: {order?.id || reservationId || "--"}</span>
                        {showSettlementTabs ? (
                            <Tabs
                                className="zone-reservation-order-detail-tabs"
                                items={[
                                    {
                                        key: "info",
                                        label: "预约信息",
                                        children: <ReservationInfoContent detail={detail} />
                                    },
                                    {
                                        key: "weighbill",
                                        label: "装船过磅单",
                                        children: <WeighbillContent detail={detail} />
                                    },
                                    {
                                        key: "handover",
                                        label: "货物交接单",
                                        children: (
                                            <HandoverContent detail={detail} signatureUrl={handoverSignatureUrl} />
                                        )
                                    }
                                ]}
                            />
                        ) : (
                            <ReservationInfoContent detail={detail} />
                        )}
                    </>
                ) : null}
                {showAuditAction || showEditAction || showCancelAction || showWeightAction || showEditWeighbillAction ? (
                    <Space className="operation-fixed-btn zone-reservation-order-detail-actions" size={0}>
                        {showAuditAction ? (
                            <Button
                                aria-label="审核"
                                disabled={buttonBanned}
                                type="primary"
                                onClick={() => void audit()}
                            >
                                审核
                            </Button>
                        ) : null}
                        {showEditAction ? (
                            <Button aria-label="编辑" disabled={buttonBanned} type="primary" onClick={openEdit}>
                                编辑
                            </Button>
                        ) : null}
                        {showCancelAction ? (
                            <Button
                                aria-label="取消"
                                danger
                                disabled={buttonBanned}
                                type="primary"
                                onClick={() => void cancel()}
                            >
                                取消
                            </Button>
                        ) : null}
                        {showWeightAction ? (
                            <Button
                                aria-label="磅单录入"
                                disabled={buttonBanned}
                                type="primary"
                                onClick={() => setWeighDialogMode("create")}
                            >
                                磅单录入
                            </Button>
                        ) : null}
                        {showEditWeighbillAction ? (
                            <Button
                                aria-label="编辑过磅单"
                                disabled={buttonBanned}
                                type="primary"
                                onClick={() => setWeighDialogMode("edit")}
                            >
                                编辑过磅单
                            </Button>
                        ) : null}
                        <Button aria-label="返回" onClick={() => navigate(-1)}>
                            返回
                        </Button>
                    </Space>
                ) : null}
                {/* 保留日志数据和无障碍契约；旧 Vue 详情页不展示该模块，因此只在视觉层隐藏。 */}
                <Card className="mt-4 zone-reservation-order-operation-log" title="操作日志">
                    <OperationLogContent loading={operationLogLoading} logs={operationLogs} />
                </Card>
                <ZhDialog
                    open={editOpen}
                    title="编辑预约单"
                    width="920px"
                    onCancel={() => setEditOpen(false)}
                    footer={
                        <Space>
                            <Button onClick={() => setEditOpen(false)}>取消</Button>
                            <Button
                                aria-label="保存"
                                loading={editSubmitting}
                                type="primary"
                                onClick={() => void submitEdit()}
                            >
                                保存
                            </Button>
                        </Space>
                    }
                >
                    {detail ? (
                        <div className="max-h-[calc(70vh-120px)] overflow-y-auto overflow-x-hidden">
                            <section aria-label="合同信息" className="mb-4">
                                <div className="mb-3 text-sm font-semibold">合同信息</div>
                                <ZhMultiGrid>
                                    <ZhGrid cols={2}>
                                        <ZhBaseItem label="合同吨数" value={getContractTotalTon(detail)} />
                                        <ZhBaseItem label="专区名称" value={getZoneLabel(detail)} />
                                        <ZhBaseItem label="合同日期" value={getContractDateRange(detail)} />
                                        <ZhBaseItem label="可预约金额" value={getAvailableBalance(detail)} />
                                    </ZhGrid>
                                </ZhMultiGrid>
                            </section>

                            <section aria-label="物料信息" className="mb-4">
                                <div className="mb-3 text-sm font-semibold">物料信息</div>
                                <ZhMultiGrid>
                                    {detail.specialZoneReservationOrderGoods?.length ? (
                                        detail.specialZoneReservationOrderGoods.map((goods, index) => (
                                            <ZhGrid
                                                key={`${goods.id || goods.goodsCode}-${index}`}
                                                cols={2}
                                                gap={[32, 0]}
                                            >
                                                <ZhGridItem>
                                                    <ZhBaseItem
                                                        label="物料名称"
                                                        value={reservationMaterialName(goods.goodsCode)}
                                                    />
                                                </ZhGridItem>
                                                <ZhGridItem>
                                                    <ZhBaseItem
                                                        label="单价"
                                                        value={`${formatMoney(goods.unitPrice)} 元`}
                                                    />
                                                </ZhGridItem>
                                                <ZhGridItem>
                                                    <ZhBaseItem
                                                        label="计划提货量"
                                                        value={`${Number(goods.plannedTonnage || 0).toLocaleString("zh-CN")} 吨`}
                                                    />
                                                </ZhGridItem>
                                                <ZhGridItem>
                                                    <ZhBaseItem
                                                        label="预占金额"
                                                        value={formatMoney(reservationMaterialPreemptPrice(goods))}
                                                    />
                                                </ZhGridItem>
                                            </ZhGrid>
                                        ))
                                    ) : (
                                        <ZhGrid cols={2} gap={[32, 0]}>
                                            <ZhGridItem>
                                                <ZhBaseItem label="物料名称" value="--" />
                                            </ZhGridItem>
                                            <ZhGridItem>
                                                <ZhBaseItem label="单价" value="--" />
                                            </ZhGridItem>
                                            <ZhGridItem>
                                                <ZhBaseItem label="计划提货量" value="--" />
                                            </ZhGridItem>
                                            <ZhGridItem>
                                                <ZhBaseItem label="预占金额" value="--" />
                                            </ZhGridItem>
                                        </ZhGrid>
                                    )}
                                </ZhMultiGrid>
                            </section>

                            <section aria-label="船舶信息">
                                <div className="mb-3 text-sm font-semibold">船舶信息</div>
                                <Form form={editForm} layout="vertical">
                                    <ZhMultiGrid>
                                        <ZhGrid cols={2} gap={[32, 0]}>
                                            <ZhGridItem>
                                                <ZhBaseItem
                                                    label="关联船运需求/船运单"
                                                    value={
                                                        pickReservationTransportDisplay(
                                                            order?.transportOrderShipId,
                                                            order?.requirementPlatId
                                                        ) || "--"
                                                    }
                                                />
                                            </ZhGridItem>
                                            <ZhGridItem>
                                                <Form.Item
                                                    label="船名"
                                                    name="shipName"
                                                    rules={[{ required: true, message: "请输入船名" }]}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </ZhGridItem>
                                            <ZhGridItem>
                                                <Form.Item label="联系方式" name="mobile">
                                                    <Input />
                                                </Form.Item>
                                            </ZhGridItem>
                                            <ZhGridItem>
                                                <Form.Item
                                                    label="目的地"
                                                    name="destinationAddress"
                                                    rules={[{ required: true, message: "请输入目的地" }]}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </ZhGridItem>
                                            <ZhGridItem>
                                                <Form.Item label="目的港" name="destinationPort">
                                                    <Input />
                                                </Form.Item>
                                            </ZhGridItem>
                                            <ZhGridItem>
                                                <Form.Item
                                                    label="预到日期"
                                                    name="reservationTime"
                                                    rules={[{ required: true, message: "请输入预到日期" }]}
                                                >
                                                    <ZhDatePicker
                                                        aria-label="预到日期"
                                                        placeholder="请选择预到日期"
                                                        type="date"
                                                        valueFormat="YYYY-MM-DD"
                                                    />
                                                </Form.Item>
                                            </ZhGridItem>
                                        </ZhGrid>
                                    </ZhMultiGrid>
                                </Form>
                            </section>
                        </div>
                    ) : null}
                </ZhDialog>
                {detail && weighDialogMode ? (
                    <WeighingListEntryDialog
                        open
                        mode={weighDialogMode}
                        waybillNo={pickReservationTransportDisplay(order?.transportOrderShipId, order?.requirementPlatId)}
                        transportOrderShipId={order?.transportOrderShipId || undefined}
                        requirementPlatId={order?.requirementPlatId || undefined}
                        shipId={order?.shipId || undefined}
                        shipName={order?.shipName || detail.ship?.cnname || detail.ship?.name || ""}
                        loadingEndTime={weighingDialogTime}
                        attachmentFileIds={weighingDialogAttachmentFileIds}
                        lines={weighingDialogLines}
                        onCancel={() => setWeighDialogMode(null)}
                        onSubmit={value => void submitWeighing(value)}
                    />
                ) : null}
            </Card>
        </section>
    );
}

export default ZoneReservationOrderDetailPage;
